import Page from "../../Page.jsx";
import {DataGrid, GridToolbarQuickFilter} from "@mui/x-data-grid";
import React, {useState} from "react";
import {Box, Button, CircularProgress} from "@mui/material";
import {useKeyAccounts, useKeyAccountsConfig} from "./KeyAccountsApproval.hooks.jsx";
import {SubscriptionStatusModal} from "./SubscriptionStatusModal.jsx";
import {handleForceSendClick} from "./KeyAccountsApproval.handlers.js";
import {DoNotSendCheckbox} from "./DoNotSendCheckBox.jsx";
import {getApprovalStatus} from "./utils.js";
import {ComboBox} from "../../comboBox/ComboBox.jsx";
import {useFetchGeoFamilyIds} from "../data-subscription/DataSubscription.hooks.jsx";
import {getAccessToken} from "../../../api/getAccessToken.js";

const salesFormatter = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});
const COLUMNS = [
    {
        field: "createdAt",
        headerName: "Production Date",
        flex: 1,
        renderCell: (params) => {
            return (new Date(params?.formattedValue)).toLocaleDateString("en-CA")
        },
        valueGetter: (params) => {
            return params.value ? new Date(params.value) : undefined
        }
    },
    {
        field: "companyName",
        headerName: "Co/Brand",
        flex: 1
    },
    {
        field: "brands",
        headerName: "Brands",
        flex: 1,
        renderCell: (params) => {
            return <div>{params.value?.join(', ')}</div>
        }
    },
    {
        field: "retailerName",
        headerName: "Retailer",
        flex: 1
    },
    {
        field: "spinsOwner",
        headerName: "SPINS Owner",
        flex: 1
    },
    {
        field: "accountManager",
        headerName: "Account Manager",
        flex: 1
    },
    {
        field: "categoryName",
        headerName: "Category",
        flex: 1
    },
    {
        field: "totalSalesForGeographyCategory",
        headerName: "52 Week Category Sales",
        flex: 1,
        renderCell: (params) => {
            return <div>{salesFormatter.format(params.formattedValue)}</div>
        }
    },
    {
        field: "totalSalesForGeography",
        headerName: "52 Week Retailer Sales",
        flex: 1,
        renderCell: (params) => {
            return <div>{salesFormatter.format(params.formattedValue)}</div>
        }
    },
    {
        field: "status",
        headerName: "KA Approval Status",
        flex: 1,
        renderCell: params => getApprovalStatus(params.row)
    },
    {
        field: "statusUpdatedAt",
        headerName: "Status Updated On",
        flex: 1,
        renderCell: (params) => {
            return params?.formattedValue ? (new Date(params.formattedValue)).toLocaleDateString("en-CA") : ""
        },
        valueGetter: (params) => {
            return params.value ? new Date(params.value) : undefined
        }
    },
    {
        field: "statusUpdatedBy",
        headerName: "Status Updated By",
        flex: 1
    },
    {
        field: "approverEmailedSentAt",
        headerName: "Date Sent",
        flex: 1,
        renderCell: (params) => {
            return params?.formattedValue ? (new Date(params.formattedValue)).toLocaleDateString("en-CA") : ""
        },
        valueGetter: (params) => {
            return params.value ? new Date(params.value) : undefined
        }
    },
    {
        field: "disabledMailer",
        headerName: "Do Not Send Mail",
        flex: 1,
        type: 'boolean',
        editable: true,
        renderCell: (params) => <DoNotSendCheckbox params={params}/>
    },
    {
        field: "lastModifiedBy",
        headerName: "Last Modified By",
        flex: 1
    },
]


function QuickSearchToolbar() {
    return (
        <Box sx={{p: 1, pb: 0,}}
             style={{display: "flex", justifyContent: "end", paddingBottom: "10px", borderBottom: "1px solid gray"}}
        >
            {
                process.env.REACT_APP_ENV !== "prod" &&
                <Button
                    style={{display: "flex", marginRight: "5px", height: "33px"}}
                    variant="contained"
                    data-testid={"force-send-button"}
                    onClick={() => handleForceSendClick(getAccessToken())}
                >
                    Force Send Mail
                </Button>
            }
            <GridToolbarQuickFilter
                debounceMs={500}
                size={"medium"}
                data-testid={"datagrid-quick-filter"}
                quickFilterParser={(searchInput) => {
                    return [searchInput];
                }}
                variant="outlined"
                sx={{paddingBottom: 0}}
            />
        </Box>
    );
}

export const KeyAccountsApproval = () => {
    const {rows, loading, fetchAllSubscriptions} = useKeyAccounts();

    const [selectedSubscriptions, setSelectedSubscriptions] = useState([])
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
    const {geoFamilyIds, isGeoFamilyIdsLoading} = useFetchGeoFamilyIds();
    const {keyAccountConfig, updateKeyAccountConfig} = useKeyAccountsConfig();


    const handleClose = () => {
        fetchAllSubscriptions().finally(() => {
            setIsStatusModalOpen(false)
        })
    }

    return (
        <Page name={"Key Accounts Approval"}>
            {isGeoFamilyIdsLoading ? (
                <CircularProgress data-testid={'key-accounts-config-loading'}/>
            ) : (
                <ComboBox
                    testId={"key-accounts-config"}
                    label={"Send to Account Managers Only"}
                    value={keyAccountConfig}
                    isMultiple={true}
                    disableCloseOnSelect={true}
                    data={geoFamilyIds}
                    getOptionKeyImplementation={(option) => option.value}
                    onChange={(event, value) => {
                        updateKeyAccountConfig(value)
                    }}
                />
            )}
            <Button
                disabled={selectedSubscriptions.length < 1}
                style={{display: "flex", margin: "12px 0"}}
                variant="contained"
                onClick={() => setIsStatusModalOpen(true)}
            >
                Edit Status
            </Button>
            <DataGrid
                checkboxSelection
                columns={COLUMNS}
                rows={rows}
                slots={{toolbar: QuickSearchToolbar}}
                getRowId={(row) => row.id}
                loading={loading}
                autoHeight={true}
                disableVirtualization={!!process.env.REACT_APP_DISABLE_VIRTUALIZATION}
                data-testid={'key-accounts-list-data-grid'}
                columnBuffer={7}
                onRowSelectionModelChange={(ids) => {
                    const selectedRows = rows.filter(row => ids.includes(row.id))
                    setSelectedSubscriptions(selectedRows);
                }}
            />

            <SubscriptionStatusModal
                isOpen={isStatusModalOpen}
                handleClose={handleClose}
                subscriptions={selectedSubscriptions}
            />

        </Page>
    )
}
